import React, { useState } from "react";

import {
    ArrowForwardOutlined,
    Close
} from "@mui/icons-material";

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography
} from "@mui/material";

import AxiosInstance from "../../services/AxiosInstance";
import { useAlert } from "../alerts/AlertContext";
import { CHANGE_REQUEST_URL } from "../../Constants/Constants";

const MoveChangeDialog = ({
    open,
    change,
    currentStage,
    nextStage,
    onClose,
    onSuccess
}) => {

    const showAlert = useAlert();

    const [moving, setMoving] =
        useState(false);


    /*
     * Move change
     */
    const handleMove = async () => {

        if (
            !change?.id ||
            !nextStage?.code
        ) {
            return;
        }

        setMoving(true);

        try {

            const response =
                await AxiosInstance.post(
                    `${CHANGE_REQUEST_URL}/${change.id}/workflow/move`,
                    {
                        toStatusCode:
                            nextStage.code
                    }
                );


            showAlert(
                `Change moved to ${nextStage.name}`,
                "success"
            );


            onSuccess?.(
                response.data
            );

        } catch (error) {

            console.error(
                "Unable to move change",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to move change",
                "error"
            );

        } finally {

            setMoving(false);

        }
    };


    /*
     * Close
     */
    const handleClose = () => {

        if (moving) {
            return;
        }

        onClose?.();

    };


    return (

        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >

                <Typography
                    variant="h6"
                    fontWeight={600}
                >
                    Move Change
                </Typography>


                <IconButton
                    onClick={handleClose}
                    disabled={moving}
                >
                    <Close />
                </IconButton>

            </DialogTitle>


            <DialogContent dividers>

                <Stack spacing={3}>

                    <Typography
                        variant="body1"
                    >
                        Move change{" "}
                        <strong>
                            {change?.changeNumber || "-"}
                        </strong>
                        {" "}to the next workflow stage?
                    </Typography>


                    {/* Current → Next */}

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            py: 2
                        }}
                    >

                        <Box
                            sx={{
                                textAlign: "center"
                            }}
                        >

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Current Stage
                            </Typography>

                            <Box sx={{ mt: 1 }}>

                                <Chip
                                    label={
                                        currentStage?.name ||
                                        change?.statusName ||
                                        "-"
                                    }
                                />

                            </Box>

                        </Box>


                        <ArrowForwardOutlined
                            color="action"
                        />


                        <Box
                            sx={{
                                textAlign: "center"
                            }}
                        >

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Next Stage
                            </Typography>

                            <Box sx={{ mt: 1 }}>

                                <Chip
                                    label={
                                        nextStage?.name ||
                                        "-"
                                    }
                                    color="primary"
                                />

                            </Box>

                        </Box>

                    </Box>


                    {/* Change information */}

                    <Box
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 2
                        }}
                    >

                        <Stack spacing={1.5}>

                            <Box>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Change Type
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                >
                                    {change?.changeTypeName || "-"}
                                </Typography>

                            </Box>


                            <Box>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Risk
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                >
                                    {change?.riskName || "-"}
                                </Typography>

                            </Box>


                            <Box>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Affected Service
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                >
                                    {change?.affectedServiceName || "-"}
                                </Typography>

                            </Box>

                        </Stack>

                    </Box>


                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        The workflow history will record this
                        status transition.
                    </Typography>

                </Stack>

            </DialogContent>


            <DialogActions
                sx={{
                    px: 3,
                    py: 2
                }}
            >

                <Button
                    onClick={handleClose}
                    disabled={moving}
                >
                    Cancel
                </Button>


                <Button
                    variant="contained"
                    onClick={handleMove}
                    disabled={
                        moving ||
                        !nextStage
                    }
                    startIcon={
                        moving
                            ? (
                                <CircularProgress
                                    size={18}
                                />
                            )
                            : (
                                <ArrowForwardOutlined />
                            )
                    }
                >
                    {moving
                        ? "Moving..."
                        : `Move to ${nextStage?.name || "Next Stage"}`
                    }
                </Button>

            </DialogActions>

        </Dialog>
    );
};


export default MoveChangeDialog;